import { JobStatus } from "@lib/enums";
import { Link } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { Close } from "@mui/icons-material";
import { useRouter, usePathname } from "next/navigation";

const ProfileLink = ({ onClick }) => {
  return (
    <Link href="#" onClick={onClick}>
      View candidates
    </Link>
  );
};

const HideLink = ({ ids, handleHide }) => {
  const handleClick = (e) => {
    e.preventDefault();
    handleHide(ids);
  };
  return (
    <Link
      sx={{ position: "absolute", right: "16px", top: "9px" }}
      href="#"
      onClick={handleClick}
    >
      <Close color="secondary" />
    </Link>
  );
};

const onCandidatePage = (pathname) => pathname === "/candidates";

const showFailedMessage = (failed) => {
  return failed.every((job) => !Boolean(job.metadata?.candidate_id));
};

export default function useMessage(jobs, handleHide) {
  const client = useQueryClient();
  const router = useRouter();
  const pathname = usePathname();

  const queued = jobs.filter((job) => job.status === JobStatus.QUEUED);
  const pending = jobs.filter((job) => job.status === JobStatus.PENDING);
  const importingPersonal = jobs.filter(
    (job) => job.status === JobStatus.IMPORTING_PERSONAL_DETAILS
  );
  const importingCV = jobs.filter(
    (job) => job.status === JobStatus.IMPORTING_CV
  );
  const complete = jobs.filter((job) => job.status === JobStatus.COMPLETE);
  const failed = jobs.filter((job) => job.status === JobStatus.FAILED);

  const completedIds = complete.map((job) => job.id);

  const handleComplete = useCallback(
    (e) => {
      e.preventDefault();
      handleHide(completedIds);
      client.invalidateQueries({ queryKey: ["candidates"] });
      router.push("/candidates");
    },
    [completedIds, handleHide, client, router]
  );

  let link = null;
  let message = "";
  let showLoader = false;

  if (queued.length > 0) {
    message =
      queued.length === 1
        ? "Sending a CV to our AI"
        : `Sending ${queued.length} CVs to our AI`;
    showLoader = true;
  } else if (pending.length > 0) {
    message = "Queued for AI processing";
    showLoader = true;
  } else if (importingPersonal.length > 0) {
    message =
      importingPersonal.length === 1
        ? "Extracting data from the CV"
        : "Extracting data from CVs";
    showLoader = true;
  } else if (importingCV.length > 0) {
    message =
      importingCV.length === 1
        ? "Transforming your CV into a profile"
        : "Transforming your CVs into profiles";
    showLoader = true;
  } else if (complete.length > 0) {
    message = `Success! New candidate profile${
      complete.length > 1 ? "s" : ""
    } created.`;
    link = (
      <>
        {!onCandidatePage(pathname) && (
          <ProfileLink
            importedCount={complete.length}
            onClick={handleComplete}
          />
        )}
        <HideLink ids={completedIds} handleHide={handleHide} />
      </>
    );
  } else if (failed.length > 0 && showFailedMessage(failed)) {
    message =
      failed.length === 1
        ? "😔 1 CV failed to process"
        : `😔 ${failed.length} CVs failed to process`;
    link = (
      <HideLink ids={failed.map((job) => job.id)} handleHide={handleHide} />
    );
  }

  return { showLoader, message, link };
}
